import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Grid,
  Table,
  Button,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Box,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import '../../assets/css/buttons.css';

import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/Context';

import { SocketContext } from '../../context/SocketContext';
import { useTranslation } from 'react-i18next';

import TableLoader from '../../components/TableLoader/TableLoader';

const headerCellSx = {
  backgroundColor: '#151d3b',
  color: '#8fa3d4',
  fontWeight: 700,
  fontSize: '13px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '2px solid #2a3a6e',
  whiteSpace: 'nowrap',
};

function Spillplan() {
  const { hall, printer } = useContext(AppContext);
  const { socket } = useContext(SocketContext);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { isLogin, isBlocking, BlockingScreen } = useContext(AppContext);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const [rows, setRows] = useState(false);
  const [rowsFiltered, setRowsFiltered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [winnerModal, setWinnerModal] = useState(null);

  const [gameType, setGameType] = useState('superbingo');

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') event.preventDefault();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!isLogin) {
      navigate('/login');
    }
  }, []);

  useEffect(() => {
    socket.emit('getRopPW', { action: 'getRopPW' });
  }, [hall]);

  useEffect(() => {
    const handleGetRopPW = (data) => {
      setRows(data);
      setRowsFiltered(data);
      setIsLoading(false);
    };

    const handleGetRopKingo = (data) => {
      setRows(data);
      setRowsFiltered(data);
      setIsLoading(false);
    };

    const handleGetRopXtipp = (data) => {
      setRows(data);
      setRowsFiltered(data);
      setIsLoading(false);
    };

    socket.on('getRopPW', handleGetRopPW);
    socket.on('getRopKingo', handleGetRopKingo);
    socket.on('getRopXtipp', handleGetRopXtipp);
    return () => {
      socket.off('getRopPW', handleGetRopPW);
      socket.off('getRopKingo', handleGetRopKingo);
      socket.off('getRopXtipp', handleGetRopXtipp);
    };
  }, [socket]);

  useEffect(() => {
    setIsLoading(true);
    setWinnerModal(null);
    switch (gameType) {
      case 'superbingo':
        socket.emit('getRopPW', { action: 'getRopPW', provider: gameType });
        break;
      case 'kingo':
        socket.emit('getRopKingo', {
          action: 'getRopKingo',
          provider: gameType,
        });
        break;
      case 'xtipp':
        socket.emit('getRopXtipp', {
          action: 'getRopXtipp',
          provider: gameType,
        });
        break;
      default:
        break;
    }
  }, [gameType]);

  // Printing is only allowed once the game's end time (winning.dateEnd,
  // a "hh:mm:ss" string) has passed for today. Mirrors the backend's
  // checkIfGameIsFinished logic.
  const isGameFinished = (row) => {
    const endTimeStr = row?.winning?.dateEnd;
    if (!endTimeStr) return false;
    const [h, m, s] = endTimeStr.split(':').map(Number);
    const now = new Date();
    const endToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      h || 0,
      m || 0,
      s || 0,
      0
    );
    return now >= endToday;
  };

  const handlePrintGame = (gameID, name, numbers, priceCard) => {
    console.log('handlePrintGame called with:', {
      gameID,
      name,
      numbers,
      priceCard,
      printer: printerRef.current,
      game:
        gameType === 'superbingo'
          ? 'PowerBingo'
          : gameType === 'kingo'
            ? 'Kingo'
            : 'Xtipp',
    });
    socket.emit('printDrawGamePlan', {
      gameID: gameID,
      name: name,
      numbers: numbers,
      priceCard: priceCard,
      printer: printerRef.current,
      game:
        gameType === 'superbingo'
          ? 'PowerBingo'
          : gameType === 'kingo'
            ? 'Kingo'
            : 'Xtipp',
    });
  };

  return (
    <>
      {isBlocking && <BlockingScreen />}

      {/* WINNERS MODAL */}
      {winnerModal && (
        <div id="react-confirm-alert">
          <div className="react-confirm-alert-overlay">
            <div className="react-confirm-alert">
              <div
                className="react-confirm-alert-body"
                style={{ width: '900px', color: '#000' }}
              >
                <h1>
                  {winnerModal.name
                    ? `${winnerModal.name} — ${t('gameplan.winners')}`
                    : t('gameplan.winners')}
                </h1>
                <p style={{ color: '#555', marginBottom: '8px' }}>
                  {t('gameplan.gameID')}: <strong>{winnerModal.gameID}</strong>
                  &nbsp;·&nbsp;
                  {t('gameplan.dateStart')}:{' '}
                  <strong>{winnerModal.winning.dateStart}</strong>
                  &nbsp;·&nbsp;
                  {t('gameplan.dateEnd')}:{' '}
                  <strong>{winnerModal.winning.dateEnd}</strong>
                </p>

                <Paper
                  elevation={3}
                  sx={{
                    backgroundColor: 'white',
                    padding: 2,
                    borderRadius: 2,
                    color: 'black',
                  }}
                >
                  <Box
                    className="sp-scroll"
                    sx={{ maxHeight: 400, overflow: 'auto' }}
                  >
                    <Table
                      size="small"
                      sx={{
                        '& thead th': {
                          fontWeight: 'bold',
                          backgroundColor: '#f0f0f0',
                          color: '#000',
                        },
                        '& tbody tr:nth-of-type(odd)': {
                          backgroundColor: 'rgba(0,0,0,0.02)',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {gameType === 'superbingo' && (
                            <TableCell align="center">
                              {t('gameplan.prizeType')}
                            </TableCell>
                          )}
                          {gameType === 'superbingo' && (
                            <TableCell align="center">
                              {t('gameplan.rop')}
                            </TableCell>
                          )}
                          <TableCell align="center">
                            {t('gameplan.cardNumber')}
                          </TableCell>
                          <TableCell align="center">
                            {t('gameplan.userID')}
                          </TableCell>
                          <TableCell align="center">
                            {t('gameplan.bingoName')}
                          </TableCell>
                          <TableCell align="center">
                            {t('gameplan.playFrom')}
                          </TableCell>
                          <TableCell align="center">
                            {t('gameplan.win')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {winnerModal.winning.winners.map((w, wi) => (
                          <TableRow key={wi}>
                            {gameType === 'superbingo' && (
                              <TableCell
                                align="center"
                                style={{ color: '#000', fontWeight: 'bold' }}
                              >
                                {w.prizeType}
                              </TableCell>
                            )}
                            {gameType === 'superbingo' && (
                              <TableCell
                                align="center"
                                style={{ color: '#000' }}
                              >
                                {w.rop}
                              </TableCell>
                            )}
                            <TableCell align="center" style={{ color: '#000' }}>
                              {w.cardNumber}
                            </TableCell>
                            <TableCell align="center" style={{ color: '#000' }}>
                              {w.userID}
                            </TableCell>
                            <TableCell align="center" style={{ color: '#000' }}>
                              {w.bingoName}
                            </TableCell>
                            <TableCell align="center" style={{ color: '#000' }}>
                              {w.playFrom}
                            </TableCell>
                            <TableCell
                              align="center"
                              style={{ color: '#000', fontWeight: 'bold' }}
                            >
                              {w.win} kr
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>

                <div
                  className="react-confirm-alert-button-group"
                  style={{ marginTop: '24px' }}
                >
                  <button
                    className="btn-custom-yes"
                    onClick={() => setWinnerModal(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <Grid
            container
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {/* GAME SELECTOR */}
            <Grid
              item
              xs={12}
              sx={{
                height: '120px',
                maxHeight: '120px',
                display: 'flex',
                alignItems: 'center',
                px: 3,
                borderBottom: '1px solid #2a3a6e',
              }}
            >
              <FormControl
                sx={{
                  minWidth: 260,
                  backgroundColor: '#232a45',
                  borderRadius: '4px',
                }}
              >
                <InputLabel
                  sx={{ color: 'white !important', fontSize: '20px' }}
                >
                  {t('sales.provider')}
                </InputLabel>
                <MuiSelect
                  value={gameType}
                  label={t('sales.provider')}
                  onChange={(e) => setGameType(e.target.value)}
                  sx={{
                    color: 'white',
                    height: '72px',
                    fontSize: '22px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#365ab8',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fcad89',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fcad89',
                    },
                    '& .MuiSvgIcon-root': { color: 'white' },
                  }}
                >
                  <MenuItem value="superbingo">PowerBingo</MenuItem>
                  <MenuItem value="kingo">Kingo</MenuItem>
                  <MenuItem value="xtipp">Xtipp</MenuItem>
                </MuiSelect>
              </FormControl>
            </Grid>

            {/*TABLE*/}
            <Grid
              item
              xs={12}
              sx={{
                width: '100%',
                padding: '10px',
                height: '100%',
              }}
            >
              {isLoading ? (
                <TableLoader />
              ) : (
                <TableContainer
                  component={Paper}
                  className="sp-scroll"
                  sx={{
                    maxHeight: 'calc(100% - 120px)',
                    marginBottom: '120px',
                    backgroundColor: '#1a2240',
                  }}
                >
                  <Table
                    sx={{
                      color: 'white',
                      background: '#1a2240',
                    }}
                    stickyHeader
                    aria-label="simple table"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" sx={headerCellSx}>
                          {t('gameplan.dateStart')}
                        </TableCell>
                        <TableCell align="center" sx={headerCellSx}>
                          {t('gameplan.gameID')}
                        </TableCell>
                        {gameType === 'superbingo' && (
                          <TableCell align="center" sx={headerCellSx}>
                            {t('gameplan.name')}
                          </TableCell>
                        )}
                        {gameType === 'superbingo' && (
                          <TableCell align="center" sx={headerCellSx}>
                            {t('gameplan.priceCard')}
                          </TableCell>
                        )}
                        <TableCell align="center" sx={headerCellSx}>
                          {t('gameplan.winners')}
                        </TableCell>
                        <TableCell align="center" sx={headerCellSx}></TableCell>
                      </TableRow>
                    </TableHead>
                    {rowsFiltered && (
                      <TableBody>
                        {rowsFiltered.map((row, index) => {
                          const hasWinners = row.winning?.winners?.length > 0;

                          return (
                            <TableRow
                              key={index}
                              sx={{
                                '&:last-child td, &:last-child th': {
                                  border: 0,
                                },
                                '&:nth-of-type(odd)': {
                                  backgroundColor: '#232a45',
                                },
                                '&:nth-of-type(even)': {
                                  backgroundColor: '#1a2036',
                                },
                              }}
                            >
                              <TableCell
                                component="th"
                                scope="row"
                                align="center"
                              >
                                {row.dateStart
                                  ? new Date(row.dateStart).toLocaleTimeString(
                                      'no-NO',
                                      {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false,
                                      }
                                    )
                                  : '—'}
                              </TableCell>
                              <TableCell align="center">{row.gameID}</TableCell>
                              {gameType === 'superbingo' && (
                                <TableCell align="center">{row.name}</TableCell>
                              )}
                              {gameType === 'superbingo' && (
                                <TableCell align="center">
                                  {row.priceCard}
                                </TableCell>
                              )}
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!hasWinners}
                                  onClick={() => setWinnerModal(row)}
                                  sx={{
                                    borderColor: hasWinners
                                      ? '#4ade80'
                                      : '#2a3a6e',
                                    color: hasWinners ? '#4ade80' : '#4a5578',
                                    '&:hover': {
                                      borderColor: '#4ade80',
                                      backgroundColor: 'rgba(74,222,128,0.08)',
                                    },
                                    '&.Mui-disabled': {
                                      borderColor: '#2a3a6e',
                                      color: '#4a5578',
                                    },
                                  }}
                                >
                                  {hasWinners
                                    ? row.winning.winners.length
                                    : '—'}
                                </Button>
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled={!isGameFinished(row)}
                                  onClick={() => {
                                    handlePrintGame(
                                      row.gameID,
                                      row.name,
                                      row.numbers,
                                      row.priceCard
                                    );
                                  }}
                                >
                                  {t('gameplan.printDraw')}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default Spillplan;
